import React from "react";
import { Badge } from "../ui/badge.tsx";

export type EntityStatus =
  | "pending"
  | "processing"
  | "processed"
  | "published"
  | "rejected"
  | "manual_review"
  | "failed"
  | "skipped"
  | "publishing"
  | "connected"
  | "disconnected"
  | "active"
  | "inactive"
  | "working"
  | "stopped"
  | "scan_qr_code"
  | "healthy"
  | "warning"
  | "error"
  | "offline";

interface StatusBadgeProps {
  status: EntityStatus | string;
  customLabel?: string;
  className?: string;
}

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning";
    dotClass?: string;
  }
> = {
  // Ofertas
  pending: { label: "Pendente", variant: "warning", dotClass: "bg-amber-400" },
  processing: { label: "Processando", variant: "secondary", dotClass: "bg-blue-400 animate-pulse" },
  processed: { label: "Pronto p/ Envio", variant: "secondary", dotClass: "bg-sky-400" },
  published: { label: "Publicado", variant: "success", dotClass: "bg-emerald-400" },
  rejected: { label: "Rejeitado", variant: "destructive", dotClass: "bg-rose-400" },
  manual_review: { label: "Revisão manual", variant: "warning", dotClass: "bg-orange-400" },
  failed: { label: "Falhou", variant: "destructive", dotClass: "bg-rose-500" },

  // Publicações
  publishing: {
    label: "Enviando...",
    variant: "secondary",
    dotClass: "bg-indigo-400 animate-pulse",
  },
  skipped: { label: "Ignorado", variant: "outline", dotClass: "bg-slate-400" },

  // WAHA / Conexão
  connected: { label: "Conectado", variant: "success", dotClass: "bg-emerald-500" },
  working: { label: "Operando", variant: "success", dotClass: "bg-emerald-500" },
  disconnected: { label: "Desconectado", variant: "destructive", dotClass: "bg-rose-500" },
  stopped: { label: "Parado", variant: "outline", dotClass: "bg-slate-400" },
  scan_qr_code: {
    label: "Aguardando QR Code",
    variant: "warning",
    dotClass: "bg-amber-400 animate-pulse",
  },
  offline: { label: "Offline", variant: "destructive", dotClass: "bg-rose-500" },

  // Status de Entidade (Grupos, etc)
  active: { label: "Ativo", variant: "success", dotClass: "bg-emerald-500" },
  inactive: { label: "Inativo", variant: "outline", dotClass: "bg-slate-400" },

  // Diagnósticos
  healthy: { label: "Saudável", variant: "success", dotClass: "bg-emerald-500" },
  warning: { label: "Alerta", variant: "warning", dotClass: "bg-amber-400" },
  error: { label: "Erro", variant: "destructive", dotClass: "bg-rose-500" },
};

export function StatusBadge({ status, customLabel, className }: StatusBadgeProps) {
  const normalized = String(status).toLowerCase();
  const config = STATUS_CONFIG[normalized] || {
    label: customLabel || status,
    variant: "outline" as const,
    dotClass: "bg-slate-400",
  };

  return (
    <Badge variant={config.variant} className={className}>
      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${config.dotClass}`} />
      {customLabel || config.label}
    </Badge>
  );
}
